import React, { createContext, useState, useEffect, useContext } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import api from '../services/api';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [initializing, setInitializing] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tenantSelectionRequired, setTenantSelectionRequired] = useState(false);
  const [availableTenants, setAvailableTenants] = useState([]);
  // Sucursal activa (null = matriz / la propia empresa del usuario). Solo
  // aplica a admins de grupo; hace que todas las peticiones envien _tenant_id.
  const [activeSucursal, setActiveSucursalState] = useState(null);

  const isSuperAdmin = user?.es_super_admin === true;
  // Admin de grupo: puede crear y cambiar entre sucursales.
  const isGrupoAdmin = user?.rol === 'admin' && !!user?.grupo_id;

  useEffect(() => {
    const loadStoredUser = async () => {
      try {
        const storedUser = await AsyncStorage.getItem('user');
        const token = await AsyncStorage.getItem('userToken');
        const storedTenant = await AsyncStorage.getItem('tenantInfo');

        if (storedUser && token) {
          setUser(JSON.parse(storedUser));
          if (storedTenant) {
            setTenant(JSON.parse(storedTenant));
          }
          const sucId = await AsyncStorage.getItem('activeSucursalId');
          const sucNombre = await AsyncStorage.getItem('activeSucursalNombre');
          if (sucId) {
            setActiveSucursalState({ id: parseInt(sucId), nombre: sucNombre || 'Sucursal' });
          }
        }
      } catch (e) {
        console.log('Error al cargar usuario:', e);
      } finally {
        setInitializing(false);
      }
    };

    loadStoredUser();
  }, []);

  const DEV_MODE = false;

  const login = async (username, password, tenantSlug = null) => {
    try {
      setError(null);
      setLoading(true);
      setTenantSelectionRequired(false);

      if (DEV_MODE) {
        const mockUser = { id: 1, email: username, nombre: 'Usuario de Prueba', rol: 'admin', tenant_id: 1, tenant_nombre: 'Empresa Principal', tenant_slug: 'principal', es_super_admin: false };
        await AsyncStorage.setItem('userToken', 'dev-token-12345');
        await AsyncStorage.setItem('user', JSON.stringify(mockUser));
        await AsyncStorage.setItem('tenantInfo', JSON.stringify({ id: 1, nombre: 'Empresa Principal', slug: 'principal' }));
        setUser(mockUser);
        setTenant({ id: 1, nombre: 'Empresa Principal', slug: 'principal' });
        return true;
      }

      const response = await api.login(username, password, tenantSlug);

      // Si el backend pide seleccion de tenant
      if (response.requireTenantSlug) {
        setTenantSelectionRequired(true);
        setAvailableTenants(response.tenants || []);
        return false;
      }

      await AsyncStorage.setItem('userToken', response.token);
      await AsyncStorage.setItem('user', JSON.stringify(response.user));

      const tenantInfo = {
        id: response.user.tenant_id,
        nombre: response.user.tenant_nombre,
        slug: response.user.tenant_slug
      };
      await AsyncStorage.setItem('tenantInfo', JSON.stringify(tenantInfo));

      setUser(response.user);
      setTenant(tenantInfo);
      setTenantSelectionRequired(false);
      setAvailableTenants([]);
      return true;
    } catch (e) {
      console.error('Error de inicio de sesion:', e);
      setError(e.message || 'Error al iniciar sesion');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Cambia la sucursal activa. `sucursal` = { id, nombre, es_matriz } o null.
  // Si es la matriz (o null) se limpia el _tenant_id y todo vuelve a la empresa
  // principal. Las pantallas recargan sus datos al volver a enfocarse.
  const switchSucursal = async (sucursal) => {
    try {
      if (!sucursal || sucursal.es_matriz) {
        await AsyncStorage.removeItem('activeSucursalId');
        await AsyncStorage.removeItem('activeSucursalNombre');
        setActiveSucursalState(null);
      } else {
        await AsyncStorage.setItem('activeSucursalId', String(sucursal.id));
        await AsyncStorage.setItem('activeSucursalNombre', sucursal.nombre || 'Sucursal');
        setActiveSucursalState({ id: sucursal.id, nombre: sucursal.nombre || 'Sucursal' });
      }
    } catch (e) {
      console.log('Error al cambiar de sucursal:', e);
    }
  };

  const logout = async () => {
    try {
      await AsyncStorage.removeItem('userToken');
      await AsyncStorage.removeItem('user');
      await AsyncStorage.removeItem('tenantInfo');
      await AsyncStorage.removeItem('activeSucursalId');
      await AsyncStorage.removeItem('activeSucursalNombre');
      setUser(null);
      setTenant(null);
      setActiveSucursalState(null);
      setError(null);
      setTenantSelectionRequired(false);
      setAvailableTenants([]);
      console.log('Sesion cerrada correctamente');
    } catch (e) {
      console.log('Error al cerrar sesion:', e);
    }
  };

  const checkTokenValidity = async () => {
    try {
      const token = await AsyncStorage.getItem('userToken');
      if (!token) return false;
      if (token === 'dev-token-12345') return true;

      const parts = token.split('.');
      if (parts.length !== 3) {
        await logout();
        return false;
      }

      const base64Payload = parts[1];
      const decodedPayload = base64Payload.replace(/-/g, '+').replace(/_/g, '/');
      const decoded = atob(decodedPayload);
      const tokenPayload = JSON.parse(decoded);
      const currentTime = Date.now() / 1000;

      if (tokenPayload.exp && tokenPayload.exp <= currentTime) {
        await logout();
        return false;
      }

      return true;
    } catch (e) {
      return true;
    }
  };

  const updateUserProfile = async (profileData) => {
    try {
      if (DEV_MODE) {
        const updatedUser = { ...user, ...profileData };
        await AsyncStorage.setItem('user', JSON.stringify(updatedUser));
        setUser(updatedUser);
        return updatedUser;
      }

      const response = await api.updateUserProfile(user.id, profileData);
      const updatedUser = { ...user, ...response.user };
      await AsyncStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);
      return updatedUser;
    } catch (error) {
      console.error('Error al actualizar perfil:', error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{
      user, tenant, initializing, loading, error, isSuperAdmin,
      isGrupoAdmin, activeSucursal, switchSucursal,
      tenantSelectionRequired, availableTenants,
      login, logout, checkTokenValidity, updateUserProfile
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};

export default AuthContext;
